import { isInternalEmail } from './recipients';
import { graphFetch } from '../graph/client';
import { GROUP_SEARCH_SCOPES } from '../graph/directory';
import { getAllStores } from './stores';
import { getAllUserRoles } from './userRoles';
import { equalsIgnoreCase } from './scope';

/**
 * Who a task can be ASSIGNED to: actual app users only (owner ruling
 * 2026-09-16 — assigning to a non-user produces emails whose task link the
 * recipient can't open). The set is the union of:
 *  - active UserRoles USER rows,
 *  - members of active UserRoles GROUP rows (expanded via Graph — same
 *    Group.Read.All the admin picker uses, requested incrementally; a
 *    group that can't be expanded is skipped rather than failing the list),
 *  - active stores' shared accounts (assignable by definition — they see
 *    their own store by account match).
 * Notify-CC pickers deliberately stay full-directory (PrincipalField).
 */

export interface Assignee {
  upn: string;
  name: string;
  /** 'store' rows label with the store for recognizability. */
  kind: 'user' | 'store';
}

export interface AssignableUsers {
  people: Assignee[];
  /** Role groups whose members could not be read — surfaced in the picker. */
  unexpandedGroups: number;
}

interface GroupMember {
  displayName: string;
  /** Absent on non-user members (nested groups, devices). */
  userPrincipalName?: string;
}

async function groupMembers(groupId: string): Promise<GroupMember[]> {
  // Transitive = matches how the token's groups claim treats nesting.
  // No OData cast segment — some tenants reject it; non-user members are
  // filtered by the absence of a UPN instead. $top=999 covers any sane
  // access group; paging deliberately skipped.
  const res = await graphFetch<{ value: GroupMember[] }>(
    `/groups/${groupId}/transitiveMembers?$select=displayName,userPrincipalName&$top=999`,
    { scopes: GROUP_SEARCH_SCOPES },
  );
  return res.value.filter((m) => Boolean(m.userPrincipalName));
}

let cache: Promise<AssignableUsers> | null = null;

/** Cached for the app session — role/store changes are rare. */
export function getAssignableUsers(): Promise<AssignableUsers> {
  if (!cache) {
    cache = buildAssignableUsers();
    cache.catch(() => {
      cache = null; // allow retry after a transient failure
    });
  }
  return cache;
}

async function buildAssignableUsers(): Promise<AssignableUsers> {
  const [roles, stores] = await Promise.all([
    getAllUserRoles(),
    getAllStores(), // active only — divested stores don't get new tasks
  ]);
  const active = roles.filter((r) => r.active);

  const list: Assignee[] = [];
  const byKey = new Map<string, Assignee>();
  const add = (
    upn: string,
    name: string,
    kind: Assignee['kind'],
    override = false,
  ) => {
    const clean = upn.trim();
    // Only people AuditPlus may email (G2) — guests/onmicrosoft accounts
    // from group expansion would be refused when the task is saved.
    if (!clean || !isInternalEmail(clean)) return;
    const key = clean.toLowerCase();
    const existing = byKey.get(key);
    if (existing) {
      // A DIRECT user assignment is the deliberate identity — its display
      // name beats a store label sharing the UPN (found in testing: the
      // owner's UPN on a test store's account field made him unfindable
      // by name). Group-member entries never override.
      if (override) {
        existing.name = name.trim() || existing.name;
        existing.kind = kind;
      }
      return;
    }
    const entry: Assignee = { upn: clean, name: name.trim() || clean, kind };
    byKey.set(key, entry);
    list.push(entry);
  };

  // Store accounts first so a UPN that's ONLY a store gets the store label.
  for (const s of stores) {
    add(s.managerEmail, `${s.storeNumber} — ${s.name}`, 'store');
  }
  for (const r of active) {
    if (r.principalType === 'user') add(r.upn, r.displayName, 'user', true);
  }
  const groups = active.filter(
    (r) => r.principalType === 'group' && r.groupId.trim() !== '',
  );
  const expansions = await Promise.allSettled(
    groups.map((g) => groupMembers(g.groupId.trim())),
  );
  let unexpandedGroups = 0;
  for (const res of expansions) {
    if (res.status !== 'fulfilled') {
      unexpandedGroups += 1; // consent/permission gap — surfaced by the picker
      continue;
    }
    for (const m of res.value) {
      add(m.userPrincipalName ?? '', m.displayName, 'user');
    }
  }

  const people = list.sort((a, b) =>
    equalsIgnoreCase(a.name, b.name)
      ? a.upn.localeCompare(b.upn)
      : a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
  );
  return { people, unexpandedGroups };
}
